<template>
  <div class="blue lighten-4">
    <v-card v-if="formData && selectLists" class="blue lighten-4">
      <v-card-title>
        <DialogTitle :dialogName="$options.name">
          <div class="d-flex">
            <div class="subtitle-1 ml-2">
              {{ isEditMode ? 'Edit' : 'New' }} Service For Site:
              {{ formData.SiteName }}
            </div>
            <v-spacer />
            <ActivityLogButton
              v-if="isEditMode"
              :description="
                `Scheduled Service For Site ${
                  formData.SiteName
                } on ${toLocaleDateString(formData.ServiceDate)}`
              "
              tableName="ScheduledService"
              :recordId="formData.ScheduledServiceId"
            />
          </div>
        </DialogTitle>
      </v-card-title>
      <v-card-text class="blue lighten-4">
        <div>
          <div>
            <v-card>
              <v-card-title>
                <div class="text-subtitle-1 blue--text">
                  Service Info
                </div>
              </v-card-title>
              <v-card-subtitle v-if="routeOrRecurring">
                <div v-if="formData.ServiceRouteCode !== null">
                  <span>Route </span>
                  <span class="text-subtitle-2 purple--text">
                    {{ formData.ServiceRouteCode }}
                  </span>
                </div>
                <div class="d-flex">
                  <div class="mt-2">Recurs</div>
                  <div class="text-subtitle-2 purple--text mt-2 ml-1">
                    {{ formData.RepeatInfo }}
                  </div>
                  <v-spacer />
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-if="formData.RecurringServiceId"
                        icon
                        @click="recurringServiceClick"
                      >
                        <v-icon
                          :color="feature.services.color"
                          v-bind="attrs"
                          v-on="on"
                        >
                          {{ feature.recurringServices.icon }}
                        </v-icon>
                      </v-btn>
                    </template>
                    <span>Edit Recurring Service</span>
                  </v-tooltip>
                </div>
              </v-card-subtitle>
              <v-card-subtitle v-else-if="formData.WorkOrderNumber">
                <span>Work Order Number</span>
                <span class="text-subtitle-2 purple--text">
                  {{ formData.WorkOrderNumber }}
                </span>
              </v-card-subtitle>
              <v-card-text>
                <SitePicker
                  label="Site"
                  v-model="formData.SiteId"
                  v-bind:text.sync="formData.SiteName"
                  readonly
                  :isServiceEditEnabled="false"
                  :error-messages="errorMessages(vuelidate.formData.SiteId)"
                  dense
                  @blur="vuelidate.formData.SiteId.$touch()"
                />
                <ServiceTypePicker
                  v-model="formData.ServiceDefId"
                  :error-messages="
                    errorMessages(vuelidate.formData.ServiceDefId)
                  "
                  @blur="vuelidate.formData.ServiceDefId.$touch()"
                />
                <div class="d-flex">
                  <DatePicker
                    v-model="formData.ServiceDate"
                    label="Service Date"
                    :error-messages="
                      errorMessages(vuelidate.formData.ServiceDate)
                    "
                    dense
                    @blur="vuelidate.formData.ServiceDate.$touch()"
                  />
                  <NearbyServicesButton
                    v-if="formData.SiteId"
                    :siteId="formData.SiteId"
                    :serviceDate="formData.ServiceDate || todayISOString()"
                    :serviceTypeId="formData.ServiceDefId"
                    @update="nearbyServiceSelected"
                  />
                </div>
                <DateTimePicker
                  v-model="formData.WhenStarted"
                  label="When Started"
                  dense
                />
                <UserPicker
                  v-model="formData.UserId"
                  label="Assigned To"
                  clearable
                  :error-messages="errorMessages(vuelidate.formData.UserId)"
                  @blur="vuelidate.formData.UserId.$touch()"
                />
                <v-text-field
                  v-model="formData.StopNumber"
                  type="number"
                  label="Stop Number"
                  dense
                />
                <div class="d-flex flex-wrap">
                  <v-currency-field
                    v-if="formData.IsAmountOverride"
                    v-model="formData.AmountToCharge"
                    label="Service Amount"
                    clearable
                    dense
                  />
                  <div v-else class="mt-1">
                    Amount: {{ toCurrency(formData.AmountToCharge) }}
                  </div>
                  <v-spacer />
                  <v-checkbox
                    v-model="formData.IsAmountOverride"
                    label="Override"
                  />
                </div>
                <v-textarea
                  v-model="formData.Comments"
                  label="Comments"
                  rows="3"
                  dense
                />
                <v-textarea
                  v-if="formData.RecurringServiceComments"
                  readonly
                  v-model="formData.RecurringServiceComments"
                  label="Recurring Service Comments"
                  dense
                />
              </v-card-text>
            </v-card>
            <v-card class="mt-2">
              <v-card-text>
                <v-card-actions>
                  <v-btn color="success" @click="saveClick">
                    <span>Save</span>
                    <v-icon right>{{ icon.save }}</v-icon>
                  </v-btn>
                  <v-btn class="ml-2" color="error" @click="cancelClick">
                    <span>Back</span>
                    <v-icon right>{{ icon.exit }}</v-icon>
                  </v-btn>
                  <v-spacer></v-spacer>
                  <v-btn
                    v-if="formData.ScheduledServiceId"
                    color="primary"
                    @click="showInvoice"
                  >
                    <span>Invoice</span>
                    <v-icon right>{{ icon.invoice }}</v-icon>
                  </v-btn>
                </v-card-actions>
              </v-card-text>
            </v-card>
          </div>

          <div class="mt-1">
            <v-tabs
              v-model="selectedTab"
              background-color="#BBDEFB"
              show-arrows
            >
              <v-tab :key="tabValue.siteinfo">
                Site Info
              </v-tab>
              <v-tab :key="tabValue.checklist" :disabled="!checklistEligible">
                Checklist
              </v-tab>
              <v-tab
                :key="tabValue.measures"
                :disabled="formData.ScheduledServiceId === null"
              >
                Measures
              </v-tab>
              <v-tab
                :key="tabValue.treatments"
                :disabled="formData.ScheduledServiceId === null"
              >
                Treatments
              </v-tab>
              <v-tab
                :key="tabValue.photos"
                :disabled="formData.ScheduledServiceId === null"
              >
                Photos
              </v-tab>

              <v-tab :key="tabValue.status">Status</v-tab>

              <v-tab
                :key="tabValue.notifications"
                :disabled="formData.Status === 'O'"
              >
                Notification
              </v-tab>

              <v-tab-item
                :key="tabValue.siteinfo"
                class="blue lighten-4 full-height"
              >
                <v-card min-height="340px">
                  <v-card-title>
                    <div class="text-subtitle-1 blue--text">
                      Site Info
                    </div>
                  </v-card-title>
                  <v-card-text>
                    <v-simple-table v-if="siteInfo" dense>
                      <template v-slot:default>
                        <tbody>
                          <tr
                            v-for="item in siteInfo.ExtraInfoList.filter(
                              i => i.Value
                            )"
                            :key="item.Id"
                          >
                            <td>
                              <div>{{ item.Title }}</div>
                            </td>
                            <td>
                              <div>{{ item.Value }}</div>
                            </td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </v-card-text>
                </v-card>
              </v-tab-item>

              <v-tab-item
                :key="tabValue.checklist"
                class="blue lighten-4 full-height"
              >
                <v-card min-height="340px">
                  <v-card-title>
                    <div class="text-subtitle-1 blue--text">
                      Checklist
                    </div>
                  </v-card-title>
                  <v-card-text>
                    <v-simple-table v-if="checklistFormData" dense>
                      <template v-slot:default>
                        <thead>
                          <th class="text-left">
                            <span class="ml-5">Item</span>
                          </th>
                          <th class="text-left">
                            <span class="ml-4">Previous</span>
                          </th>
                        </thead>
                        <tbody>
                          <tr
                            v-for="checklistItem in checklistFormData.Checklist"
                            :key="checklistItem.ServiceDefChecklistItemId"
                          >
                            <td>
                              <v-checkbox
                                v-model="checklistItem.IsCompleted"
                                :label="checklistItem.Description"
                                dense
                              />
                            </td>
                            <td>
                              <div v-if="checklistItem.WhenLastCompleted">
                                {{
                                  toLocaleDateString(
                                    checklistItem.WhenLastCompleted
                                  )
                                }}
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </v-card-text>
                </v-card>
              </v-tab-item>

              <v-tab-item
                :key="tabValue.measures"
                class="blue lighten-4 full-height"
              >
                <div v-if="measureTabSelected">
                  <MeasureList
                    :isDialogOpen="measureTabSelected"
                    isSummary
                    :siteId="formData.SiteId"
                    :siteDescription="formData.SiteName"
                    :scheduledServiceId="formData.ScheduledServiceId"
                  />
                </div>
              </v-tab-item>

              <v-tab-item
                :key="tabValue.treatments"
                class="blue lighten-4 full-height"
              >
                <div v-if="treatmentTabSelected">
                  <TreatmentList
                    :isDialogOpen="measureTabSelected"
                    isSummary
                    :siteId="formData.SiteId"
                    :siteDescription="formData.SiteName"
                    :scheduledServiceId="formData.ScheduledServiceId"
                  />
                </div>
              </v-tab-item>

              <v-tab-item
                :key="tabValue.photos"
                class="blue lighten-4 full-height"
              >
                <v-card>
                  <v-card-title>
                    <div class="text-subtitle-1 blue--text">
                      Photos
                    </div>
                  </v-card-title>
                  <v-card-text>
                    <PhotoList
                      :siteId="formData.SiteId"
                      :scheduledServiceId="formData.ScheduledServiceId"
                    />
                  </v-card-text>
                </v-card>
              </v-tab-item>

              <v-tab-item
                :key="tabValue.status"
                class="blue lighten-4 full-height"
              >
                <v-card>
                  <v-card-title>
                    <div class="text-subtitle-1 blue--text">
                      Service Status
                    </div>
                  </v-card-title>
                  <v-card-text>
                    <v-select
                      v-model="formData.Status"
                      :items="selectLists.ServiceStatus"
                      item-text="Text"
                      item-value="Value"
                      label="Status"
                      dense
                    />
                    <TimePicker
                      v-model="formData.EarliestArrivalTime"
                      label="Earliest Arrival Time"
                      clearable
                      dense
                    />
                    <TimePicker
                      v-model="formData.LatestArrivalTime"
                      label="Latest Arrival Time"
                      clearable
                      dense
                    />
                    <DurationPicker
                      v-model="formData.EstimatedDurationMinutes"
                      label="Estimated Duration"
                      clearable
                      dense
                    />
                    <DateTimePicker
                      v-model="formData.WhenStarted"
                      label="When Started"
                      dense
                    />
                    <DateTimePicker
                      v-model="formData.WhenCompleted"
                      label="When Completed"
                      dense
                    />
                    <v-textarea
                      v-if="formData.Status === 'C'"
                      v-model="formData.CompletionNotes"
                      label="Completion Notes"
                      dense
                    />
                  </v-card-text>
                </v-card>
              </v-tab-item>

              <v-tab-item
                :key="tabValue.notifications"
                class="blue lighten-4 full-height"
              >
                <div>
                  <v-card min-height="340px" class="mr-2 mb-2">
                    <v-card-title>
                      <div class="text-subtitle-1 blue--text">
                        Notification
                      </div>
                    </v-card-title>
                    <v-card-text>
                      <v-checkbox
                        v-model="formData.SendEmail"
                        label="Send Email"
                        class="mb-2"
                      />
                      <v-select
                        v-model="formData.EmailTemplateId"
                        :items="selectLists.EmailTemplates"
                        item-text="Text"
                        item-value="Value"
                        label="Email Template"
                        dense
                      />
                      <div v-if="emailTemplateInfo !== null">
                        <v-checkbox
                          v-for="insert in emailTemplateInfo.Inserts"
                          :key="insert.Name"
                          v-model="insert.IsSelected"
                          :label="insert.Name"
                          dense
                        />
                      </div>
                    </v-card-text>
                  </v-card>
                  <v-card v-if="emailTemplateInfo.Body" min-height="500px">
                    <v-card-title>
                      <div class="text-subtitle-1 blue--text">
                        Email Body
                      </div>
                    </v-card-title>
                    <v-card-text>
                      <ckeditor
                        :editor="editor"
                        v-model="formData.Body"
                        :config="{}"
                        height="400"
                      />
                    </v-card-text>
                  </v-card>
                </div>
              </v-tab-item>
            </v-tabs>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <v-dialog v-if="formData" v-model="showInvoiceDialog" persistent>
      <Invoice
        :isDialogOpen="showInvoiceDialog"
        :customerId="formData.CustomerId"
        :invoiceId="formData.InvoiceId"
        :scheduledServiceId="formData.ScheduledServiceId"
        @close="invoiceClose"
        @update="invoiceUpdate"
      />
    </v-dialog>
    <DialogUtil ref="dialogUtil" />
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { icon, feature } from '@/use/Constants'
import { selectListCache } from '@/services/SelectListCache'
import { host } from '@/services/HostAPI'
import ActivityLogButton from '@/components/Common/ActivityLogButton'
import NearbyServicesButton from '@/components/ScheduledService/NearbyServicesButton'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'
import { geoLocationLogService } from '@/services/GeoLocationLogService'
import Invoice from '@/components/Billing/Invoice'
import MeasureList from '@/components/Site/MeasureList'
import PhotoList from '@/components/Common/PhotoList'
import reportInfo from '@/constants/reports'
import ReportService from '@/services/ReportService'
import TreatmentList from '@/components/Site/TreatmentList'
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { todayISOString, toLocaleDateString } from '@/use/DateFormatter'
import { toCurrency } from '@/use/NumberFormatter'
import { isoTimeToShortTime } from '@/use/TimeFormatter'

export default {
  name: 'ScheduledServiceEditor',
  components: {
    ActivityLogButton,
    Invoice,
    MeasureList,
    NearbyServicesButton,
    PhotoList,
    SitePicker: () => import('@/components/Site/SitePicker'),
    TreatmentList
  },
  props: {
    siteId: {
      type: String,
      default: null
    },
    scheduledServiceId: {
      type: String,
      default: null
    },
    isDialogOpen: {
      type: Boolean,
      default: false
    }
  },
  setup(props, context) {
    const dialogUtil = ref(null)

    const tabValue = {
      siteinfo: 0,
      checklist: 1,
      measures: 2,
      treatments: 3,
      photos: 4,
      status: 5,
      notifications: 6
    }

    const isEditMode = ref(false)
    const selectedTab = ref(tabValue.checklist)
    const formData = ref()
    const siteInfo = ref()
    const selectLists = ref()
    const checklistFormData = ref({ Checklist: [] })
    const measureTabSelected = ref(false)
    const treatmentTabSelected = ref(false)

    const emptyEmailTemplateInfo = {
      EmailTemplateId: null,
      Bcc: null,
      Subject: null,
      Body: '',
      Inserts: []
    }

    const emailTemplateInfo = ref({ ...emptyEmailTemplateInfo })
    const showInvoiceDialog = ref(false)
    const editor = ClassicEditor

    watch(
      () => props.isDialogOpen,
      newVal => {
        newVal && initDialog()
      }
    )

    const initDialog = async () => {
      measureTabSelected.value = false
      treatmentTabSelected.value = false
      await loadSelectLists()
      isEditMode.value = props.scheduledServiceId != null
      formData.value = isEditMode.value
        ? await loadScheduledService()
        : await initNewScheduledService()
      emailTemplateInfo.value = { ...emptyEmailTemplateInfo }
      await getChecklist()
      createFormDataWatches()
      siteInfo.value = await getSiteInfo()
      selectedTab.value = tabValue.checklist
      vuelidate.value.$reset()
    }

    const loadSelectLists = async () => {
      const selectListNames = [
        selectListCache.name.Users,
        selectListCache.name.ServiceStatus,
        selectListCache.name.ServiceTypes,
        selectListCache.name.EmailTemplates,
        selectListCache.name.EmailInserts
      ]

      selectLists.value = await selectListCache.get(selectListNames)
    }

    const loadScheduledService = async () => {
      const rq = { InfoId: props.scheduledServiceId }
      const rs = await host.scheduledService.retrieve(rq)
      return rs.Info
    }

    const getSiteInfo = async () => {
      const rq = { InfoId: formData.value.SiteId }
      const rs = await host.site.retrieve(rq)
      return rs.Info
    }

    const initNewScheduledService = async () => {
      const rq = { Defaults: props.siteId }
      const rs = await host.scheduledService.newTemplate(rq)
      return rs.Info
    }

    const cancelClick = () => {
      context.emit('close')
    }

    const saveLocation = () => {
      const rq = {
        Info: {
          ScheduledServiceId: formData.value.scheduledServiceId,
          ActivityDescription: 'Close Service'
        }
      }

      geoLocationLogService.log(rq)
    }

    const saveClick = async () => {
      vuelidate.value.$touch()
      if (!vuelidate.value.$invalid) {
        if (await saveService()) {
          if (
            formData.value.ScheduledServiceId !== null &&
            formData.value.Status == 'C'
          ) {
            saveLocation()
          }
          context.emit('update')
        }
      }
    }

    const saveService = async () => {
      const rq = {
        Info: formData.value,
        Checklist: checklistFormData.value
          ? checklistFormData.value.Checklist
          : null,
        EmailTemplateInfo: emailTemplateInfo.value
      }

      const rs = isEditMode.value
        ? await host.scheduledService.extendedUpdate(rq)
        : await host.scheduledService.extendedCreate(rq)

      if (!rs.IsSuccess) {
        dialogUtil.value.error(rs.Message)
      } else {
        if (!isEditMode.value) {
          formData.value.ScheduledServiceId = rs.InfoId // eslint-disable-line require-atomic-updates
        }
      }

      return rs.IsSuccess
    }

    const printClick = async () => {
      vuelidate.value.$touch()
      if (!vuelidate.value.$invalid) {
        if (await saveService()) {
          printWorkOrder()
        }
      }
    }

    const printWorkOrder = () => {
      const workOrderReportName = selectListCache.attribute(
        selectListCache.name.ServiceTypes,
        formData.value.ServiceDefId,
        'WorkOrderReportName'
      )

      const selectedReportId =
        workOrderReportName === 'Open/Close'
          ? reportInfo.report.workorderOpenClose.reportId
          : workOrderReportName === 'Startup'
          ? reportInfo.report.workorderStartup.reportId
          : workOrderReportName === 'Repair'
          ? reportInfo.report.workorderRepair.reportId
          : reportInfo.report.workorderService.reportId

      ReportService.emitShowReportDialog({
        ReportId: selectedReportId,

        ParameterList: [
          {
            Name: 'ScheduledServiceId',
            Value: formData.value.ScheduledServiceId
          },
          {
            Name: 'ServiceDate',
            Value: formData.value.ServiceDate
          }
        ],
        ParameterHideList: [
          'ScheduledServiceId',
          'ServiceDate',
          'ReportLayout'
        ],
        EmailInfo: {
          SendEmail: false,
          CustomerId: formData.value.CustomerId,
          CustomerName: formData.value.CustomerName,
          Recipient: formData.value.Email
        }
      })
    }

    watch(
      () => selectedTab.value,
      newVal => {
        if (newVal === tabValue.checklist) {
          loadChecklistIfChanged()
        } else if (newVal === tabValue.measures) {
          measureTabSelected.value = true
        } else if (newVal === tabValue.treatments) {
          treatmentTabSelected.value = true
        }
      }
    )

    const createFormDataWatches = () => {
      watch(
        () => formData.value.Status,
        () => statusChange()
      )

      watch(
        () => formData.value.EmailTemplateId,
        newVal => {
          loadEmailTemplateInfo(newVal)
        }
      )

      watch(
        () => formData.value.SiteId,
        () => {
          loadChecklistIfChanged()
        }
      )

      watch(
        () => formData.value.ServiceDefId,
        newVal => {
          loadChecklistIfChanged()
          formData.value.EmailTemplateId = selectListCache.attribute(
            selectListCache.name.ServiceTypes,
            newVal,
            'EmailTemplateId'
          )
          if (!formData.value.IsAmountOverride) {
            formData.value.AmountToCharge = selectListCache.attribute(
              selectListCache.name.ServiceTypes,
              newVal,
              'DefaultAmountToCharge'
            )
          }
        }
      )

      watch(
        () => formData.value.ServiceDate,
        () => {
          loadChecklistIfChanged()
        }
      )
    }

    const createChecklistFormDataWatch = () => {
      watch(
        () => checklistFormData.value.Checklist,
        () => {
          applySubstitutions()
        },
        { deep: true }
      )
    }

    watch(
      () => emailTemplateInfo.value.Inserts,
      () => {
        applySubstitutions()
      },
      { deep: true }
    )

    const loadChecklistIfChanged = () => {
      if (checklistEligible.value) {
        if (
          checklistFormData.value === null ||
          checklistFormData.value.SiteId !== formData.value.SiteId ||
          checklistFormData.value.ServiceDefId !==
            formData.value.ServiceDefId ||
          checklistFormData.value.ServiceDate !== formData.value.ServiceDate
        ) {
          getChecklist()
        }
      }
    }

    const checklistEligible = computed(() => {
      return (
        formData.value.SiteId !== null &&
        formData.value.ServiceDefId !== null &&
        formData.value.ServiceDate !== null
      )
    })

    const loadEmailTemplateInfo = async id => {
      const rq = { InfoId: id }
      const rs = await host.emailTemplate.retrieve(rq)
      emailTemplateInfo.value = rs.Info
      formData.value.Bcc = rs.Info.Bcc
      formData.value.Subject = rs.Info.Subject
      formData.value.Body = rs.Info.Body
      applySubstitutions()
    }

    const getChecklist = async () => {
      const rq = {
        SiteId: formData.value.SiteId,
        ServiceDefId: formData.value.ServiceDefId,
        ServiceDate: formData.value.ServiceDate,
        ScheduledServiceId: formData.value.ScheduledServiceId
      }
      const rs = await host.scheduledService.getChecklist(rq)
      checklistFormData.value = rs
      createChecklistFormDataWatch()
    }

    const applySubstitutions = () => {
      if (!emailTemplateInfo.value || !formData.value.Body) {
        return
      }

      const advisories =
        '<ul>' +
        emailTemplateInfo.value.Inserts.filter(element => element.IsSelected)
          .map(element => '<li>' + element.Text + '</li>')
          .join('') +
        '</ul>'

      const checklistItems =
        checklistFormData.value && checklistFormData.value.Checklist
          ? '<ul>' +
            checklistFormData.value.Checklist.filter(
              element => element.IsCompleted
            )
              .map(element => '<li>' + element.Description + '</li>')
              .join('') +
            '</ul>'
          : ''

      formData.value.Body = emailTemplateInfo.value.Body.replace(
        '{0}',
        advisories
      )
        .replace('{1}', formData.value.CustomerName)
        .replace('{2}', formData.value.SiteName)
        .replace('{3}', formData.value.CompletionNotes)
        .replace('{4}', checklistItems)
        .replace(
          '{7}',
          selectListCache.attribute(
            selectListCache.name.Users,
            formData.value.UserId,
            'Text'
          )
        )
        .replace(
          '{8}',
          selectListCache.attribute(
            selectListCache.name.ServiceTypes,
            formData.value.ServiceDefId,
            'Text'
          )
        )
        .replace('{9}', formData.value.RepeatInfo)
        .replace('{11}', formData.value.Comments ?? '')
        .replace(
          '{12}',
          isoTimeToShortTime(formData.value.WhenStarted) ?? '(n/a)'
        )
        .replace(
          '{13}',
          isoTimeToShortTime(formData.value.WhenCompleted) ?? '(n/a)'
        )
    }

    const showInvoice = () => {
      showInvoiceDialog.value = true
    }

    const invoiceClose = () => {
      showInvoiceDialog.value = false
    }

    const invoiceUpdate = event => {
      formData.value.InvoiceId = event
      showInvoiceDialog.value = false
    }

    const routeOrRecurring = computed(() => {
      return (
        formData.value.ServiceRouteId !== null ||
        formData.value.RecurringServiceId !== null
      )
    })

    const statusChange = () => {
      if (formData.value.Status === 'C') {
        if (
          formData.value.EmailTemplateId != null &&
          emailTemplateInfo.value.EmailTemplateId == null
        ) {
          loadEmailTemplateInfo(formData.value.EmailTemplateId)
        }

        if (!formData.value.WhenCompleted) {
          formData.value.WhenCompleted = dateToISOLikeButLocal(new Date())
        }
      }
    }

    const dateToISOLikeButLocal = date => {
      const offsetMs = date.getTimezoneOffset() * 60 * 1000
      const msLocal = date.getTime() - offsetMs
      const dateLocal = new Date(msLocal)
      const iso = dateLocal.toISOString()
      const isoLocal = iso.slice(0, 19)
      return isoLocal
    }

    const deleteClick = () => {
      dialogUtil.value
        .confirm({
          title: 'Please Confirm',
          text: 'Delete this service. Are you sure?'
        })
        .then(() => deleteScheduledService())
        .catch(() => {
          return
        })
    }

    const deleteScheduledService = async () => {
      const rq = { InfoId: props.scheduledServiceId }
      const rs = await host.scheduledService.delete(rq)
      if (rs.IsSuccess) {
        context.emit('update')
      } else {
        dialogUtil.value.error(rs.Message)
      }
    }

    const nearbyServiceSelected = args => {
      formData.value.ServiceDate = args.ServiceDate
      formData.value.UserId = args.UserId
      formData.value.StopNumber = args.StopNumber + 1
      formData.value.ServiceDefId =
        formData.value.ServiceDefId || args.ServiceDefId
    }

    const rules = {
      formData: {
        SiteId: { required },
        ServiceDefId: { required },
        ServiceDate: { required },
        UserId: { required }
      }
    }

    const vuelidate = useVuelidate(rules, { formData })

    const errorMessages = item => {
      return vuelidate.value.$invalid ? item.$errors.map(e => e.$message) : []
    }

    const recurringServiceClick = () => {
      context.emit('editRecurringService', formData.value.RecurringServiceId)
      context.emit('close')
    }

    initDialog()

    return {
      dialogUtil,
      tabValue,
      isEditMode,
      selectedTab,
      editor,
      formData,
      siteInfo,
      selectLists,
      saveClick,
      cancelClick,
      deleteClick,
      checklistFormData,
      emailTemplateInfo,
      measureTabSelected,
      treatmentTabSelected,
      printClick,
      routeOrRecurring,
      showInvoiceDialog,
      invoiceClose,
      invoiceUpdate,
      showInvoice,
      checklistEligible,
      recurringServiceClick,
      nearbyServiceSelected,
      todayISOString,
      toLocaleDateString,
      toCurrency,
      icon,
      feature,
      errorMessages,
      vuelidate
    }
  }
}
</script>

<style lang="scss">
.ck-editor__editable {
  min-height: 500px;
}
</style>
